import { showToast } from "@certn/cascadia";
import {
  Text,
  Grid,
  GridItem,
  SelectItem,
  SelectFormikField,
  TextAreaFormikField,
  FileUploadFormikField,
} from "@certn/cascadia-react";
import { getPrivacyPolicyUrl } from "@certn/utils";
import { Field, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { ActionListItemWithModal } from "./ActionListItemWithModal";
import { FormModal } from "./FormModal";
import { HelpPrivacyConsent } from "./HelpPrivacyConsent";
import { useFormSubmit } from "../../../hooks/api/useSubmit";
import { baseFileTypes } from "../../../utils/file-types";

export function ReportAProblem() {
  const { t, i18n } = useTranslation();

  const privacyPolicyUrl = getPrivacyPolicyUrl(i18n.language);

  const submitForm = useFormSubmit("/api/internal/applicant/report-problem/", {
    onSuccess: () =>
      showToast(t("Submission sent"), {
        type: "info",
      }),
    raiseError: true,
  });

  const menuItems = [
    {
      name: t("Google Chrome"),
      value: "GOOGLE_CHROME",
    },
    {
      name: t("Microsoft Edge"),
      value: "MICROSOFT_EDGE",
    },
    {
      name: t("Mozilla Firefox"),
      value: "MOZILLA_FIREFOX",
    },
    {
      name: t("Other"),
      value: "OTHER",
    },
  ];

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-report-a-problem-report-button"
      title={t("Report a problem")}
      description={t(
        "Let us know if you find something that doesn't work as expected"
      )}
      link={t("VERB Report", "Report")}
    >
      {({ close }) => (
        <Formik
          onSubmit={async (values, helpers) => {
            try {
              // so we need to cut the images variable down to a list of images
              const images = values.images.map(
                (fileInfo: { content: string }) => fileInfo.content
              );
              await submitForm(
                { ...values, images },
                helpers as FormikHelpers<{
                  images: (string | undefined)[];
                  problem_description: string;
                  browser_type: string;
                }>
              );
              close();
            } catch (e) {
              console.error("Error reporting a problem:", e);
              showToast(t("Something went wrong, try again"), {
                type: "error",
              });
            }
          }}
          initialValues={{
            problem_description: "",
            browser_type: "",
            images: [],
          }}
          validationSchema={object().shape({
            problem_description: string().required(
              t("Please describe the problem.")
            ),
          })}
        >
          {({ handleSubmit }) => {
            return (
              <FormModal
                title={t("Report a problem")}
                onSubmit={handleSubmit}
                onCancel={close}
                continueText={t("Submit")}
                variant="success"
              >
                <Grid form>
                  <GridItem>
                    <Text style="global/body-primary">
                      {t(
                        "Let us know if you find something that doesn't work as expected."
                      )}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <SelectFormikField
                      name="browser_type"
                      label={t("Browser type")}
                      placeholder={t("Please select")}
                      testID="report-a-problem-modal-browser-type-select"
                    >
                      {menuItems.map((option) => (
                        <SelectItem key={option.value} id={option.value}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectFormikField>
                  </GridItem>
                  <GridItem>
                    <Field
                      isRequired
                      name="problem_description"
                      as={TextAreaFormikField}
                      aria-label="Input"
                      label={t("Describe the problem")}
                      description={t(
                        "Providing details (e.g. description, device type etc.) helps us find the problem."
                      )}
                      testID="report-a-problem-modal-describe-problem-input"
                    />
                  </GridItem>
                  <GridItem>
                    <Field
                      name="images"
                      as={FileUploadFormikField}
                      color="border-strong"
                      size="desktop"
                      label={t("Upload image or screenshot")}
                      fileTypes={baseFileTypes}
                      maxSizeBytes={10000000}
                      multiple
                      testID="report-a-problem-modal-document-upload-button"
                    />
                  </GridItem>
                  <GridItem>
                    <HelpPrivacyConsent privacyPolicyUrl={privacyPolicyUrl} />
                  </GridItem>
                </Grid>
              </FormModal>
            );
          }}
        </Formik>
      )}
    </ActionListItemWithModal>
  );
}
